import React, { useEffect, useState } from 'react';
import { Row, Col, FormInput } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import styles from '../PlantItemDetails.module.scss';
import { RefData } from 'plantItemNew/constants/refData';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';

export const MobileCraneDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;
  const {
    typeOfMobileCrane: savedTypeOfMobileCrane = '',
    otherTypeOfMobileCrane: savedOtherTypeOfMobileCrane = '',
    maxRadius: savedMaxRadius = '',
    modelDetails: savedModelDetails = '',
    maxRatedCapacity: savedMaxRatedCapacity = '',
  } = value;

  const [typeOfMobileCrane, setTypeOfMobileCrane] = useState(savedTypeOfMobileCrane);
  const [otherTypeOfMobileCrane, setOtherTypeOfMobileCrane] = useState(savedOtherTypeOfMobileCrane);
  const [maxRadius, setMaxRadius] = useState(savedMaxRadius);
  const [maxRatedCapacity, setMaxRatedCapacity] = useState(savedMaxRatedCapacity);
  const [modelDetails, setModelDetails] = useState(savedModelDetails);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'typeOfMobileCrane',
      fieldValue: typeOfMobileCrane,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'typeOfMobileCrane_other',
      fieldValue: otherTypeOfMobileCrane,
      isValid: (v) => !isEmpty(v) || typeOfMobileCrane !== 'Other (please describe)',
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'maxRadius',
      fieldValue: maxRadius,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxRatedCapacity',
      fieldValue: maxRatedCapacity,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  const mobileCraneTypeOptions = Array.isArray(refData[RefData.mobileCrane.subTypeOptions])
    ? refData[RefData.mobileCrane.subTypeOptions].map((e) => ({
        value: e.value,
        text: e.description,
      }))
    : [];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      typeOfMobileCrane,
      otherTypeOfMobileCrane,
      maxRadius,
      maxRatedCapacity,
      modelDetails,
    };
    onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [typeOfMobileCrane, otherTypeOfMobileCrane, maxRadius, maxRatedCapacity, modelDetails]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <Row>
        <Col span={4}>
          <RadioOrDropdownSelector
            id="typeOfMobileCrane"
            options={mobileCraneTypeOptions}
            title={PlantItemDetailsPage.relatedDetails.mobileCrane.typeOfMobileCraneLabel}
            errorMessage={errors?.typeOfMobileCrane?.errorMessage}
            hasError={showErrors && errors?.typeOfMobileCrane?.invalid}
            onChange={(v) => {
              setTypeOfMobileCrane(v);
              setOtherTypeOfMobileCrane('');
            }}
            value={typeOfMobileCrane}
            showExtraInput={typeOfMobileCrane === 'Other (please describe)'}
            onChangeExtraInput={setOtherTypeOfMobileCrane}
            extraInputValue={otherTypeOfMobileCrane}
            extraInputErrorMessage={errors?.typeOfMobileCrane_other?.errorMessage}
            extraInputHasError={showErrors && errors?.typeOfMobileCrane_other?.invalid}
            extraInputTitle="Other please describe"
            margin={{ top: 'md' }}
            inputWidth="lg"
            maxOptionsLengthForRadio={2}
          />

          {typeOfMobileCrane === 'Vehicle loading crane' ? (
            <FormInput
              id="modelDetails"
              label={PlantItemDetailsPage.relatedDetails.mobileCrane.modelDetailsLabel}
              onChange={(e, { value }) => setModelDetails(value)}
              value={modelDetails}
              errorMessage={errors?.modelDetails?.errorMessage}
              hasError={showErrors && errors?.modelDetails?.invalid}
            />
          ) : null}
        </Col>
      </Row>

      <Row style={{ marginTop: 20 }}>
        <Col span={3}>
          <FormInput
            id="maxRadius"
            label={PlantItemDetailsPage.relatedDetails.mobileCrane.maxRadiusLabel}
            suffix="m"
            onChange={(e, { value }) => setMaxRadius(value)}
            value={maxRadius}
            errorMessage={errors?.maxRadius?.errorMessage}
            hasError={showErrors && errors?.maxRadius?.invalid}
            className={styles.inputWithSuffix}
          />

          <FormInput
            id="maxRatedCapacity"
            label={PlantItemDetailsPage.relatedDetails.mobileCrane.maxRatedCapacityLabel}
            suffix="t"
            onChange={(e, { value }) => setMaxRatedCapacity(value)}
            value={maxRatedCapacity}
            errorMessage={errors?.maxRatedCapacity?.errorMessage}
            hasError={showErrors && errors?.maxRatedCapacity?.invalid}
            className={styles.inputWithSuffix}
          />
        </Col>
      </Row>
    </>
  );
});

MobileCraneDetails.displayName = 'MobileCraneDetails';
